import React from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, ClipboardList, FileText, BarChart3, Settings, CheckCircle2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const ASSET_BASE = process.env.REACT_APP_BACKEND_URL + "/api/assets";

const QUICK_LINKS = [
  { label: "Dispatch", path: "/dispatch", icon: CalendarDays, color: "#2563EB" },
  { label: "Jobs", path: "/jobs", icon: ClipboardList, color: "#10B981" },
  { label: "Estimates & Invoices", path: "/estimates", icon: FileText, color: "#F97316" },
  { label: "Pipeline", path: "/pipeline", icon: BarChart3, color: "#8B5CF6" },
  { label: "Calendar", path: "/calendar", icon: Settings, color: "#14B8A6" },
];

const CHECKLIST = ["Manage your jobs", "Create estimates and invoices", "Grow your business"];

export const WelcomeBanner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div
      data-testid="dashboard-welcome-banner"
      className="relative w-full rounded-2xl overflow-hidden"
      style={{ background: "linear-gradient(105deg, #0A2F6E 0%, #12569E 45%, #1A7FE8 100%)" }}
    >
      {/* Faint outdoor scene, right edge */}
      <div
        className="absolute inset-y-0 right-0 w-[42%] pointer-events-none"
        style={{
          backgroundImage: `url(${ASSET_BASE}/dashboard-welcome-scene)`,
          backgroundSize: "cover",
          backgroundPosition: "center right",
          opacity: 0.16,
          maskImage: "linear-gradient(to right, transparent, black 35%)",
          WebkitMaskImage: "linear-gradient(to right, transparent, black 35%)",
        }}
      />

      <div className="relative px-6 md:px-8 pt-4 pb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-3">
          {/* Left: welcome text */}
          <div className="min-w-0">
            <div className="flex items-start gap-2">
              <h2
                data-testid="welcome-banner-heading"
                className="text-[22px] md:text-[25px] font-bold text-white leading-none"
                style={{ fontFamily: "'Century Gothic', CenturyGothic, AppleGothic, sans-serif" }}
              >
                Welcome back,
              </h2>
              <span className="text-[19px] leading-none mt-0.5" role="img" aria-label="waving hand">👋</span>
            </div>
            <p data-testid="welcome-banner-username" className="mt-0.5 text-[13.5px] font-semibold text-white/90">
              {user?.name || "there"}
            </p>
            <div className="w-32 h-px bg-white/35 my-1.5" />
            <p className="text-[11px] text-white/80 leading-snug max-w-[280px]">
              This is your dashboard — your command center for a more efficient, profitable, and organized business.
            </p>
          </div>

          {/* Center: mascot with faint gear behind it */}
          <div className="relative flex items-center justify-center h-[88px]">
            <Settings
              className="absolute h-24 w-24 text-white/10"
              strokeWidth={1.2}
              data-testid="welcome-banner-gear-decor"
            />
            <img
              src={`${ASSET_BASE}/dashboard-welcome-mascot`}
              alt="Service Guru mascot"
              className="relative h-[88px] w-auto object-contain drop-shadow-lg"
              data-testid="welcome-banner-mascot"
            />
          </div>

          {/* Right: speech bubble + tagline */}
          <div className="flex flex-col items-end gap-1.5">
            <span
              className="italic text-white/85 text-[13px]"
              style={{ fontFamily: "'Dancing Script', cursive" }}
              data-testid="welcome-banner-tagline"
            >
              Work Smarter Every Day
            </span>
            <div className="rounded-xl rounded-tr-sm shadow-lg px-3 py-2 space-y-1 w-full max-w-[220px]" style={{ backgroundColor: "#FFFFFF" }} data-testid="welcome-banner-checklist">
              {CHECKLIST.map((item) => (
                <div key={item} className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0" style={{ color: "#10B981" }} />
                  <span className="text-[11px] font-medium" style={{ color: "#334155" }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick-link buttons */}
        <div className="mt-3 grid grid-cols-5 gap-3">
          {QUICK_LINKS.map((link) => {
            const Icon = link.icon;
            return (
              <button
                key={link.label}
                onClick={() => navigate(link.path)}
                data-testid={`welcome-banner-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="h-[76px] rounded-xl flex flex-col items-center justify-center gap-1.5 p-2 transition-all hover:-translate-y-0.5 hover:shadow-lg"
                style={{ backgroundColor: "rgba(255,255,255,0.95)" }}
              >
                <div className="h-7 w-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: link.color }}>
                  <Icon className="h-3.5 w-3.5 text-white" strokeWidth={2.2} />
                </div>
                <span className="text-[10.5px] font-bold text-center leading-tight" style={{ color: "#1e293b" }}>{link.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
